import { Injectable, TemplateRef } from '@angular/core';
import {
  AgGridCellIconComponent,
  AgGridCellPillComponent,
  DATE_COLUMN,
  EXCLUDE_COLUMN_FROM_EXPORT,
  LOCKED_COLUMN,
  NULL_DATA_STRING,
  Pill,
} from '@anduin/shared';
import { ConfigService } from '@anduin/config';
import { ColDef, ColGroupDef, IFilterDef } from 'ag-grid-community';
import {
  EMAIL_TYPE_DEFINITIONS,
  EMAIL_TYPE_GROUP_LABELS,
  EmailTypeGroupLabel,
  getEnabledEmailTypes,
  getEnabledGroupLabels,
  OutboundEmailRow,
  OutboundEmailStatus,
} from '../../../models';
import { addValueTooltip } from '../../../utils/add-value-tooltip';
import { formatTime } from '../../../utils/format-time';
import { ResendIconCellTemplateComponent } from '../../resend-icon-cell-template/resend-icon-cell-template.component';
import { ViewAttachmentsCellTemplateComponent } from '../../view-attachments-cell-template/view-attachments-cell-template.component';
import { SmtpErrorsService } from '../services';

export interface EmailDeliveryHubColumnsFactoryParams {
  onResendClick: (model: OutboundEmailRow) => Promise<unknown> | void;
  onViewAttachmentsClick: (model: OutboundEmailRow) => void;
  booleanTemplateRef: TemplateRef<HTMLElement>;
  statusTemplateRef: TemplateRef<HTMLElement>;
  bounceReasonTemplateRef: TemplateRef<HTMLElement>;
}

@Injectable()
export class EmailDeliveryHubColumnsFactory {
  constructor(
    private readonly smtpErrorsService: SmtpErrorsService,
    private readonly configService: ConfigService,
  ) {}

  create(params: EmailDeliveryHubColumnsFactoryParams): (ColDef<OutboundEmailRow> | ColGroupDef<OutboundEmailRow>)[] {
    return [
      {
        ...this._statusFilter(params.statusTemplateRef),
        field: 'status',
        width: 75,
        headerName: 'Status',
        cellRenderer: AgGridCellPillComponent,
        cellRendererParams: {
          pill: this._pillCellTemplateParams(),
        },
        type: '',
        cellStyle: { textAlign: 'center', alignItems: 'center' },
      },
      {
        ...this._emailTypeFilter(),
        field: 'emailType',
        headerName: 'Email type',
      },
      {
        field: 'subject',
        headerName: 'Subject',
        ...addValueTooltip(),
      },
      {
        field: 'recipients',
        headerName: 'Recipients',
        ...addValueTooltip(true),
      },
      {
        field: 'clientNames',
        headerName: 'Client',
      },
      {
        headerName: 'Delivered details',
        marryChildren: true,
        children: [
          {
            ...this._booleanFilter('delivered', params.booleanTemplateRef),
            field: 'delivered',
            headerName: 'Delivered',
            width: 170,
            cellRenderer: AgGridCellIconComponent,
            cellRendererParams: {
              icon: {
                width: 18,
                height: 18,
                getStroke: () => '#31AD98',
                getName: ({ value }) => (value && value !== NULL_DATA_STRING ? 'check-circle' : undefined),
                getText: ({ value }) => (value && value !== NULL_DATA_STRING ? undefined : '- -'),
              },
            },
          },
          {
            ...DATE_COLUMN(),
            field: 'deliveredAt',
            headerName: 'Delivered date',
            columnGroupShow: 'open',
            width: 160,
            valueFormatter: ({ value }) => formatTime(value),
            type: 'rightAligned',
          },
        ],
      },
      {
        headerName: 'Opened details',
        marryChildren: true,
        children: [
          {
            ...this._booleanFilter('opened', params.booleanTemplateRef),
            field: 'opened',
            headerName: 'Opened',
            width: 170,
            cellRenderer: AgGridCellIconComponent,
            cellRendererParams: {
              icon: {
                width: 18,
                height: 18,
                getStroke: () => '#31AD98',
                getName: ({ value }) => (value && value !== NULL_DATA_STRING ? 'check-circle' : undefined),
                getText: ({ value }) => (value && value !== NULL_DATA_STRING ? undefined : '- -'),
              },
            },
          },
          {
            field: 'openedAt',
            headerName: 'Opened date',
            width: 160,
            columnGroupShow: 'open',
            valueFormatter: ({ value }) => formatTime(value),
            type: 'rightAligned',
          },
        ],
      },
      {
        headerName: 'Clicked details',
        marryChildren: true,
        children: [
          {
            ...this._booleanFilter('clicked', params.booleanTemplateRef),
            field: 'clicked',
            headerName: 'Clicked',
            width: 170,
            cellRenderer: AgGridCellIconComponent,
            cellRendererParams: {
              icon: {
                width: 18,
                height: 18,
                getStroke: () => '#31AD98',
                getName: ({ value }) => (value && value !== NULL_DATA_STRING ? 'check-circle' : undefined),
                getText: ({ value }) => (value && value !== NULL_DATA_STRING ? undefined : '- -'),
              },
            },
          },
          {
            field: 'clickedAt',
            headerName: 'Clicked date',
            columnGroupShow: 'open',
            width: 160,
            valueFormatter: ({ value }) => formatTime(value),
            type: 'rightAligned',
          },
        ],
      },
      {
        headerName: 'Failure reason',
        field: 'failureReason',
        valueFormatter: (val) => this._getBounceReason(val.data),
        ...addValueTooltip(),
      },
      {
        field: 'clientIds',
        headerName: 'Client Number',
      },
      {
        field: 'clientGroupNames',
        headerName: 'Client group',
        ...addValueTooltip(true),
      },
      {
        field: 'clientPartnerNames',
        headerName: 'Partner',
        ...addValueTooltip(true),
      },
      {
        field: 'clientManagerNames',
        headerName: 'Manager',
        ...addValueTooltip(true),
      },
      {
        field: 'officeNames',
        headerName: 'Office',
        ...addValueTooltip(true),
      },
      {
        field: 'attachmentNames',
        headerName: 'Attachments',
        cellRenderer: ViewAttachmentsCellTemplateComponent,
        cellRendererParams: {
          onClick: params.onViewAttachmentsClick,
        },
      },

      // Fixed right section
      {
        ...LOCKED_COLUMN,
        headerName: 'Actions',
        type: EXCLUDE_COLUMN_FROM_EXPORT,
        pinned: 'right',
        width: 120,
        cellRenderer: ResendIconCellTemplateComponent,
        cellRendererParams: {
          onClick: params.onResendClick,
        },
      },
    ];
  }

  private _pillCellTemplateParams(): Pill {
    return {
      getValue: ({ value }) => value ?? OutboundEmailStatus.MIXED,
      getColor: ({ value }) => {
        if ([OutboundEmailStatus.DELIVERED, OutboundEmailStatus.OPENED, OutboundEmailStatus.CLICKED].includes(value)) {
          return 'green';
        } else if (value === OutboundEmailStatus.BOUNCED) {
          return 'red';
        } else if (value === OutboundEmailStatus.SENDING) {
          return 'yellow';
        } else if (value === OutboundEmailStatus.RESOLVED) {
          return 'lavender';
        }

        return 'gray';
      },
      size: 'small',
    };
  }

  private _booleanFilter(field: string, booleanTemplateRef: TemplateRef<HTMLElement>): IFilterDef {
    return {
      filter: 'customSetFilter',
      filterParams: {
        context: {
          [field]: {
            getSetFilterOptions: () => {
              return [
                { value: true, display: { templateRef: booleanTemplateRef, context: { show: true } }, checked: false },
                { value: false, display: { templateRef: booleanTemplateRef, context: { show: false } }, checked: false },
              ];
            },
          },
        },
      },
    };
  }

  private _statusFilter(statusTemplateRef: TemplateRef<HTMLElement>): IFilterDef {
    const statusOptions: { value: OutboundEmailStatus; color: string }[] = [
      { value: OutboundEmailStatus.MIXED, color: 'gray' },
      { value: OutboundEmailStatus.SENDING, color: 'yellow' },
      { value: OutboundEmailStatus.BOUNCED, color: 'red' },
      { value: OutboundEmailStatus.DELIVERED, color: 'green' },
      { value: OutboundEmailStatus.CLICKED, color: 'green' },
      { value: OutboundEmailStatus.OPENED, color: 'green' },
    ];

    return {
      filter: 'customSetFilter',
      filterParams: {
        context: {
          status: {
            getSetFilterOptions: () =>
              statusOptions.map(({ value, color }) => ({
                value,
                display: {
                  templateRef: statusTemplateRef,
                  context: { color, value },
                },
                checked: false,
              })),
          },
        },
      },
    };
  }

  private _emailTypeFilter(): IFilterDef {
    const enabledTypes = getEnabledEmailTypes(this.configService);
    const enabledGroups = getEnabledGroupLabels(this.configService);
    const sortedGroups = [...enabledGroups].sort((a, b) => {
      if (a === EMAIL_TYPE_GROUP_LABELS.MISCELLANEOUS) return 1;
      if (b === EMAIL_TYPE_GROUP_LABELS.MISCELLANEOUS) return -1;
      return a.localeCompare(b);
    });
    const groupOrder = new Map<EmailTypeGroupLabel, number>(sortedGroups.map((group, index) => [group, index]));

    const options = enabledTypes
      .map((type) => {
        const primaryGroup = EMAIL_TYPE_DEFINITIONS[type].groups.find((g) => groupOrder.has(g)) ?? EMAIL_TYPE_GROUP_LABELS.MISCELLANEOUS;
        return { value: type, group: primaryGroup };
      })
      .sort((a, b) => {
        const groupDiff = groupOrder.get(a.group as EmailTypeGroupLabel) - groupOrder.get(b.group as EmailTypeGroupLabel);
        if (groupDiff !== 0) {
          return groupDiff;
        }
        return a.value.localeCompare(b.value);
      });

    const showGroupHeaders = enabledGroups.length > 1;

    return {
      filter: 'customSetFilter',
      filterParams: {
        context: {
          emailType: {
            getSetFilterOptions: () =>
              options.map(({ value, group }) => ({
                value,
                checked: false,
                ...(showGroupHeaders ? { group } : {}),
              })),
          },
        },
      },
    };
  }

  private _getBounceReason(data: OutboundEmailRow): string {
    if (!data.bounced) {
      return NULL_DATA_STRING;
    }

    const smtpError = this.smtpErrorsService.parse(data.failureReason);
    return this.smtpErrorsService.getErrorMessage(smtpError);
  }

}
